import React, { useState } from 'react';
import { Checkbox, FormControlLabel } from '@mui/material';
import { AppContext } from '../../App';
import SelectTrainingSetWithSearch from '../extractionBatches/create/SelectTrainingSetWithSearch';
import { ITrainingSet } from '../../types/my-types';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import errorResponses from '../../errorResponses';
import useGetTrainingSet from '../hooks/useGetTrainingSet';
import { useTrainingSetUpdate } from '../hooks/useTrainingSetUpdate';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';

interface DocumentTableAddToTrainingSetProps {
  open: boolean;
  onClose: () => void;
}

function DocumentTableAddToTrainingSet({ open, onClose }: DocumentTableAddToTrainingSetProps) {
  const { setToast, dataPoolId } = React.useContext(AppContext);

  const [forwardToTrainingSetPage, setForwardToTrainingSetPage] = React.useState(false);
  const [trainingSet, setTrainingSet] = useState<ITrainingSet>();

  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const { search } = useLocation();

  const searchParams = new URLSearchParams(search);
  const selectedIds = searchParams.get('selectedIds')?.split(',') || [];

  const { mutateAsync: updateTrainingSet, isPending: isLoadingMutation } = useTrainingSetUpdate({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['trainingSet']
      });
      queryClient.invalidateQueries({
        queryKey: ['documents']
      });
      setToast({
        variant: 'success',
        open: true,
        message: 'Documents added successfully.',
      });
      onClose();
    },
    onError: (error: any) => {
      let message = error.message || 'Sorry, failed to add to training set.';
      setToast({
        variant: 'error',
        open: true,
        message,
      });
    },
  });

  const { data, isLoading: isLoadingTrainingSet } = useGetTrainingSet(
    trainingSet?.id || '',
    trainingSet !== undefined
  );

  const handleSubmit = async () => {
    if (!trainingSet || !data?.id) {
      setToast({
        open: true,
        variant: 'error',
        message: 'Unable to get training set data.',
      });
      return;
    }

    try {
      await updateTrainingSet({
        trainingSetId: data!.id,
        data: {
          documents: data.documents.concat(selectedIds),
        },
      });

      if (forwardToTrainingSetPage) {
        queryClient.invalidateQueries({ queryKey: ['trainingSets'] });
        navigate(`/data-pools/${dataPoolId}/train/${data.id}/details`);
      } else {
        onClose();
        setToast({
          open: true,
          variant: 'success',
          message: 'Training set updated successfully!',
        });
      }
    } catch (e) {
      setToast({
        open: true,
        variant: 'error',
        message: errorResponses.failedToUpdateTrainingSet,
      });
    }
  };

  return (
    <DialogGlynt
      open={open}
      closeModal={onClose}
      cypressId="dialog-add-to-training-set"
      loading={isLoadingMutation}
    >
      <DialogTitleGlynt title="Add to Existing Training Set" closeModal={onClose} loading={isLoadingMutation} />
      <DialogContentGlynt>
        <SelectTrainingSetWithSearch
          setTrainingSet={setTrainingSet}
          disabled={false}
          width="100%"
          trainedOnly={false}
        />
        <FormControlLabel
          sx={{ mt: 1.5 }}
          control={
            <Checkbox
              color="primary"
              checked={forwardToTrainingSetPage}
              inputProps={
                {
                  'data-cy': `checkbox-forward-to-training-set-page`,
                } as React.InputHTMLAttributes<HTMLInputElement>
              }
              onChange={(e) => {
                setForwardToTrainingSetPage(e.target.checked);
              }}
            />
          }
          label="Forward to Training Set Details Page?"
        />
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={handleSubmit}
        closeModal={onClose}
        cancelCypressId="training-set-add-dialog-cancel"
        submitCypressId="button-add-to-training-set-submit"
        clearCypressId=""
        submitButtonText="Submit"
        submitDisabled={isLoadingTrainingSet || !data}
        submitLoading={isLoadingMutation}
      />
    </DialogGlynt>
  );
}

export default DocumentTableAddToTrainingSet;
